import { useActiveCompany } from '~/hooks/useActiveCompany';
import { FeatureFlag, LeadSource, UserRole } from '~/types/company';

export const useFeatureFlag = (featureFlags: FeatureFlag | FeatureFlag[], whitelistedRoles?: UserRole[]) => {
  const company = useActiveCompany();
  const requiredFeatures = Array.isArray(featureFlags) ? featureFlags : [featureFlags];

  // company must have all features enabled globally
  if (!requiredFeatures.every((featureFlag) => company.features.includes(featureFlag))) return false;

  // whitelisted roles bypass user-level feature checks
  if (whitelistedRoles?.includes(company.userRole)) return true;

  return requiredFeatures.every((featureFlag) => (company.userFeatures ?? []).includes(featureFlag));
};

export const useHasInvoicing = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.Invoicing, whitelistedRoles);

export const useHasTreasury = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Treasury, whitelistedRoles);

export const useHasSeeInvoices = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.SeeInvoices, whitelistedRoles);

export const useHasPayments = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Payments, whitelistedRoles);

export const useHasAutomaticReconciliation = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AutomaticReconciliation, whitelistedRoles);

export const useHasCashflow = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Cashflow, whitelistedRoles);

export const useHasAnalyticsDashboardsPurchases = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsPurchases, whitelistedRoles);

export const useHasAnalyticsDashboardsSales = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsSales, whitelistedRoles);

export const useHasAnalyticsDashboardsBills = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsBills, whitelistedRoles);

export const useHasOutstanding = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.Outstanding, whitelistedRoles);

export const useHasHideBalances = () => {
  const value = useFeatureFlag(FeatureFlag.SeeBalances);
  const isEntrepeneur = useIsEntrepeneur();

  if (isEntrepeneur) {
    return false;
  }
  return value !== true;
};

export const useIsEntrepeneur = () => {
  const company = useActiveCompany();
  return company.userRole === UserRole.ENTREPRENEUR;
};

export const useIsPayrollConsultant = () => {
  const company = useActiveCompany();
  return company.userRole === UserRole.PAYROLL_CONSULTANT;
};

export const useIsBankAccountLeadSource = () => {
  const company = useActiveCompany();
  return company.leadSource === LeadSource.BANK_ACCOUNT;
};

export const useHasBookkeeping = () => useFeatureFlag(FeatureFlag.Bookkeeping);

export const useHasSplitTransactions = () => useFeatureFlag(FeatureFlag.SplitTransactions);

export const useHasSplitConsent = () => useFeatureFlag(FeatureFlag.SplitConsent);

export const useHasSplitByCostCenter = () => useFeatureFlag(FeatureFlag.SplitByCostCenter);

export const useHasAutomaticCategorizationTransaction = () =>
  useFeatureFlag(FeatureFlag.AutomaticCategorizationTransaction, [UserRole.ENTREPRENEUR]);

export const useHasCashback = () => {
  const company = useActiveCompany();

  // Company-level feature: once enabled for the company, all users must inherit access.
  return company.features.includes(FeatureFlag.SibillCashback);
};

export const useHasAutomaticSecondCompanyOnboarding = () =>
  useFeatureFlag(FeatureFlag.AutomaticOnboardingSecondCompanies);

export const useHasIntegrationApi = () => {
  return useFeatureFlag(FeatureFlag.IntegrationApi);
};

export const useHasAiChat = () => useFeatureFlag(FeatureFlag.AiChat);

export const useHasNewAiVatProposals = () => {
  const company = useActiveCompany();

  // Company-level feature: once enabled for the company, all users must inherit access.
  return company.features.includes(FeatureFlag.NewAiVatProposals);
};

export const useHasPayrollBookkeepingProposals = () => useFeatureFlag(FeatureFlag.PayrollBookkeepingProposals);

export const useHasSubscriptionManagement = () => useFeatureFlag(FeatureFlag.SubscriptionManagement);

export const useHasExperimentalBillsClosingDetails = () =>
  useFeatureFlag(FeatureFlag.ExperimentalBillsDashboardClosingDetails);

export const useHasCreditLines = () => useFeatureFlag(FeatureFlag.CreditLines, [UserRole.ENTREPRENEUR]);

export const useHasF24Management = () => useFeatureFlag(FeatureFlag.F24Management);
